import L from './leafletSetup';
import 'leaflet.vectorgrid/dist/Leaflet.VectorGrid.bundled.js';
import {
    AOI_COLORS,
    AOI_FILL_OPACITY,
    AOI_STROKE_WEIGHT,
    AOI_TILE_URL,
    MIN_LOAD_ZOOM,
    TILE_MAX_CONCURRENCY,
    TILE_SIZE,
    TILE_ZOOM_OFFSET,
    levelColor,
    selectedLevelColor,
    toSourceGroup,
} from '../constants';
import { message } from 'antd';
import { getToken } from 'utils/auth';
import { getEndType, getOrCreateDeviceId } from 'utils/deviceId';
import { SourceVisibility } from '../types';
import { TileBbox, tileIntersectsBbox } from './geometry';

/**
 * AOI 矢量瓦片图层。
 *
 * <p>取代原来的"整屏 bbox 拉 GeoJSON": 覆盖范围由 z/x/y 网格算出来, 不存在"一屏太多要截断"的问题,
 * 每片还能被浏览器按 URL 缓存。详见 map-service `doc/aoi-platform-v2/mvt-tiles-design.md`。
 *
 * <p><b>鉴权跟 GraphQL 一致</b>: 本项目走的是 localStorage 里的 Bearer token (不是 cookie),
 * 所以瓦片请求必须自己带 Authorization 头 —— 且要在每次取瓦片时现读, 图层是长驻的, 中途换 token
 * (续签 / 重新登录) 后不能还用建层那一刻的旧值。
 */

/** 瓦片图层暴露给页面的操作 (选中高亮 / 冲突标红 / 图层开关 / 数据版本失效)。 */
export interface AoiTileLayerHandle {
    layer: L.Layer;
    /**
     * 切换选中面 (传 null 取消)。
     *
     * <p>levelEnum 用于算选中色 (级别加深); 传 null 时用点击时缓存的级别兜底。
     * 实现必须走 setFeatureStyle 单面覆盖, 绝不能 redraw —— GridLayer 的 redraw 是
     * "丢弃全部瓦片重新请求", 每次点选整屏重拉, 瓦片加载会肉眼可见地变慢。
     */
    setSelected: (aoiId: number | null, levelEnum: string | null) => void;
    /**
     * 临时隐藏某个面 (传 null 恢复)。
     *
     * <p>编辑形状 / 有待保存的形状改动时用: 瓦片画的是库里的旧几何, 新形状由高亮层渲染,
     * 不隐藏的话两个形状叠在一起, 用户看到"编辑完还有原来的形状"的双影。
     */
    setHidden: (aoiId: number | null) => void;
    /** 保存冲突时标红的面。 */
    setConflicts: (aoiIds: number[]) => void;
    /** 来源图层开关变化后重绘。 */
    setSourceVisibility: (visibility: SourceVisibility) => void;
    /**
     * 「未生效」图层开关: 改的是瓦片 URL 参数 (includeUnverified), 所以是整层重拉 —— 和来源开关不同,
     * 来源开关只是前端隐藏, 这个是服务端多给一批面。开关值进 URL 也意味着两种口径的瓦片天然分开缓存。
     */
    setIncludeUnverified: (include: boolean) => void;
    /**
     * 写操作后推进数据版本。
     *
     * <p>带 bbox (本次编辑面的范围) 时按范围失效: 只有与 bbox 相交的瓦片换新版本 URL 冷拉,
     * 其余瓦片 URL 不变、继续命中浏览器缓存 —— "改一栋楼把全世界缓存扔掉"的旧行为只在
     * bbox 缺失时兜底。用户到服务器单程 300ms+, 这里的缓存命中率就是体感速度。
     */
    setVersion: (version: number, bbox?: TileBbox | null) => void;
    destroy: () => void;
}

export type { TileBbox } from './geometry';

interface Options {
    version: number;
    sourceVisibility: SourceVisibility;
    /** 是否请求未生效 (待审核) 面; false = 与引入生效状态之前完全一致的瓦片。 */
    includeUnverified: boolean;
    onSelect: (aoiId: number, zipcode: string | null) => void;
    /** 绘制 / 选点态下不响应面点击 (交给地图本身处理)。 */
    isPickingOrDrawing: () => boolean;
}

/** MVT feature 的属性 (对应瓦片 SQL 里的 SELECT 别名)。 */
interface TileFeatureProps {
    aoiId: number;
    /** DB level: 0 = 产品 1 级(楼栋), 1 = 产品 2 级(聚合)。 */
    level: number;
    dataSource: string | null;
    zipcode: string | null;
    /** biz_status: 1 = 已生效 / 0 = 未生效 (待人工审核, 只在开关打开时出现在瓦片里)。 */
    status: number | null;
}

/**
 * 并发闸门。
 *
 * <p>一屏 6 片会同时发出去, 高频拖动时对 api-server → Dubbo → PostGIS 是一波突发。
 * 瓦片是渐进渲染的, 排队只影响"画满要多久", 不影响可用性, 所以宁可限流也不要把下游打满。
 */
function createGate(maxConcurrent: number) {
    let running = 0;
    const queue: (() => void)[] = [];

    const release = () => {
        running -= 1;
        const next = queue.shift();
        if (next) {
            running += 1;
            next();
        }
    };

    return function run<T>(task: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const start = () => {
                task().then(resolve, reject).finally(release);
            };
            if (running < maxConcurrent) {
                running += 1;
                start();
            } else {
                queue.push(start);
            }
        });
    };
}

export function createAoiTileLayer(options: Options): AoiTileLayerHandle {
    let selectedId: number | null = null;
    let hiddenId: number | null = null;
    let conflictIds = new Set<number>();
    let visibility = options.sourceVisibility;
    /** 点击时记下 feature 的 level: 外部 setSelected 时 detail 可能还没回来, 用它算选中色。 */
    const levelByAoiId = new Map<number, number>();

    /* ---------- 按范围失效的版本管理 ---------- */

    /** 全局基线版本: 只有"没有 bbox 的失效"(兜底) 才推进它。 */
    let baseVersion = options.version;
    /** 已应用过的最大版本号 (幂等: 同版本重复 setVersion 不重绘)。 */
    let lastAppliedVersion = options.version;
    /** 范围失效记录: 与 bbox 相交的瓦片用 v 作版本, 其余沿用旧 URL 命中浏览器缓存。 */
    const invalidations: { bbox: TileBbox; v: number }[] = [];
    /**
     * bbox 外扩量 (度, ~200m): 服务端 ST_AsMVTGeom 裁剪带 64/4096 缓冲,
     * 面稍出瓦片边界仍会被邻片渲染, 失效范围要把这圈缓冲盖住, 否则邻片留旧影。
     */
    const INVALIDATION_PAD_DEG = 0.002;

    /** 该瓦片应使用的版本号 = max(基线, 所有压到它的范围失效)。 */
    const versionFor = (coords: { z: number; x: number; y: number }): number => {
        let v = baseVersion;
        for (const inv of invalidations) {
            if (inv.v > v && tileIntersectsBbox(coords.z, coords.x, coords.y, inv.bbox)) {
                v = inv.v;
            }
        }
        return v;
    };

    const gate = createGate(TILE_MAX_CONCURRENCY);

    /**
     * 单个面的基础样式: 冲突红 > 级别色 (1级青 / 2级紫); 来源被关掉 / 编辑中则整片隐藏。
     * 选中态不在这里: 它通过 setFeatureStyle 单面覆盖实现 (见 setSelected), 避免整层 redraw 重拉瓦片。
     */
    const styleFor = (props: TileFeatureProps): L.PathOptions => {
        const group = toSourceGroup(props.dataSource);
        // 编辑中的面隐藏 (旧几何由高亮层的新形状顶替); 来源被关掉的面也隐藏
        if (props.aoiId === hiddenId || visibility[group] === false) {
            // VectorGrid 没有"跳过这个 feature"的口子, 用全透明代替 (仍在 DOM 里, 但不可见不可点)
            return { opacity: 0, fillOpacity: 0, interactive: false, weight: 0 };
        }
        const conflict = conflictIds.has(props.aoiId);
        const levelEnum = props.level === 0 ? 'LEVEL_1' : 'LEVEL_2';
        const color = conflict ? AOI_COLORS.CONFLICT.hex : levelColor(levelEnum).hex;
        // 未生效 (待审核) = 待办, 必须比已生效更抢眼。
        //
        // 原来是反的: 未生效 fillOpacity 0.05 + 虚线, 已生效 0.14 实线 —— 按"还没进业务"这个
        // 业务语义的强弱分配视觉权重, 结果要审的那个被画得比不关心的更弱, 运营反馈"区分起来很累"。
        // 审核场景要按任务重要性分配。而且"虚线"这个通道本身就选错了: 线型必须聚焦到边界才分得出
        // (中央凹任务, 只能逐个面看), 填充面积则是前注意通道, 余光扫一眼整屏成像, 两者效率差一个量级;
        // 小面 / 低 zoom 下虚线还会糊成实线。
        //
        // 所以主信号改成填充 (0.05 → 0.35), 线宽辅助加粗, 虚线保留但降级为"辅助确认"。
        // 只加强未生效、<b>不动已生效</b>: 「未生效」图层关着时地图与从前完全一致, 零回归。
        // 颜色不动 —— 色相通道归"级别"用 (1级青 / 2级紫), 换色要动图例语义, 收益不抵复杂度。
        // 冲突红优先, 冲突面无论生效与否都要醒目。
        const unverified = props.status === 0 && !conflict;
        return {
            color,
            weight: conflict
                ? AOI_STROKE_WEIGHT.HIGHLIGHT
                : unverified ? AOI_STROKE_WEIGHT.UNVERIFIED : AOI_STROKE_WEIGHT.VERIFIED,
            opacity: 0.95,
            fill: true,
            fillColor: color,
            fillOpacity: conflict
                ? AOI_FILL_OPACITY.HIGHLIGHT
                : unverified ? AOI_FILL_OPACITY.UNVERIFIED : AOI_FILL_OPACITY.VERIFIED,
            dashArray: unverified ? '6 4' : undefined,
        };
    };

    /** 选中态样式 = 级别色加深 + 粗边 (级别未知时深灰兜底)。 */
    const selectedStyle = (levelEnum: string | null): L.PathOptions => {
        const color = selectedLevelColor(levelEnum).hex;
        return {
            color,
            weight: 3,
            opacity: 0.95,
            fill: true,
            fillColor: color,
            fillOpacity: 0.26,
        };
    };

    /** 隐藏样式 (编辑中的面): 全透明。 */
    const HIDDEN_STYLE: L.PathOptions = { opacity: 0, fillOpacity: 0, weight: 0 };

    /** 冲突标红样式 (保存重叠时后端返回的冲突面)。 */
    const CONFLICT_STYLE: L.PathOptions = {
        color: AOI_COLORS.CONFLICT.hex,
        weight: 3,
        opacity: 0.95,
        fill: true,
        fillColor: AOI_COLORS.CONFLICT.hex,
        fillOpacity: 0.26,
    };

    /** 选中面已解析的级别 (隐藏解除后恢复选中样式时要用)。 */
    let selectedLevelResolved: string | null = null;

    /**
     * 按当前状态刷新某个面的覆盖样式。
     *
     * <p>setFeatureStyle 的覆盖优先级高于 vectorTileLayerStyles(styleFor), 且 VectorGrid 会把它
     * 存下来对后续加载的瓦片同样生效 —— 所以隐藏/冲突/选中全走这一个覆盖槽, 互斥关系集中在这里:
     * 隐藏 > 冲突 > 选中 > 无覆盖(交还 styleFor)。全程零网络请求, 绝不 redraw ——
     * GridLayer 的 redraw 是"丢弃全部瓦片整屏重新下载", 只允许留给来源图层开关这种全量场景。
     */
    const applyFeatureOverride = (aoiId: number) => {
        try {
            if (aoiId === hiddenId) {
                layer.setFeatureStyle(aoiId, HIDDEN_STYLE);
            } else if (conflictIds.has(aoiId)) {
                layer.setFeatureStyle(aoiId, CONFLICT_STYLE);
            } else if (aoiId === selectedId) {
                layer.setFeatureStyle(aoiId, selectedStyle(selectedLevelResolved));
            } else {
                layer.resetFeatureStyle(aoiId);
            }
        } catch {
            /* 不在当前瓦片里 */
        }
    };

    // {v} 由下面的取瓦片包装按片填充 (versionFor), 不再是全局一个值;
    // {includeUnverified} 是「未生效」图层开关, 进 URL 让两种口径的瓦片分开缓存
    const layer: any = (L as any).vectorGrid.protobuf(`${AOI_TILE_URL}?v={v}&includeUnverified={includeUnverified}`, {
        v: options.version,
        includeUnverified: options.includeUnverified ? 'true' : 'false',
        // 512px 瓦片: 一屏从 ~15 片降到 ~6 片, 请求数降到 1/3。
        // 注意这里**不能**传 zoomOffset —— 那是 L.TileLayer 的选项 (只在 _getZoomForUrl 里生效),
        // VectorGrid 自己拼 URL 时直接用 coords.z, 完全无视它。偏移必须我们自己在下面补, 见 tileCoordsWithOffset。
        tileSize: TILE_SIZE,
        // 必须高于底图 (TileLayer 默认 zIndex=1): 底图和瓦片同在 tilePane, 不设的话
        // 切换到卫星图/Google 时新底图 addTo 排在 DOM 后面, 直接盖住 AOI 轮廓 ——
        // 而且切回 OSM 时 OSM 重新 add 也排后面, 轮廓就"再也回不来"了。
        zIndex: 5,
        minZoom: MIN_LOAD_ZOOM,
        maxNativeZoom: 20,
        interactive: true,
        getFeatureId: (feature: { properties: TileFeatureProps }) => feature.properties.aoiId,
        vectorTileLayerStyles: {
            // 图层名要跟 ST_AsMVT(src, 'aoi', ...) 里的名字一致
            aoi: (props: TileFeatureProps) => styleFor(props),
        },
    });

    /**
     * 修正瓦片层级。
     *
     * <p>tileSize=512 时 Leaflet 的瓦片网格是按 512 切的, x/y 落在 z-1 的编号体系里 (世界像素宽
     * 256·2^z 除以 512 = 2^(z-1) 片), 但 `coords.z` 仍是地图 zoom。VectorGrid 拼 URL 直接用 coords.z,
     * 于是发出去的是 "z=14 + z13 的 x/y" —— 服务端 ST_TileEnvelope 算出来是另一块地方的框,
     * 每片都空, 表现为整屏一个面都不显示。这里把 z 补回来。
     */
    const tileCoordsWithOffset = (coords: any) => {
        const fixed = new (L as any).Point(coords.x, coords.y);
        fixed.z = coords.z + TILE_ZOOM_OFFSET;
        return fixed;
    };

    const authHeaders = () => {
        const token = getToken();
        return {
            ...(token ? { authorization: `Bearer ${token}` } : {}),
            DeviceInfo: JSON.stringify({ deviceUUID: getOrCreateDeviceId(), endType: getEndType() }),
        };
    };

    /**
     * 首片探针。
     *
     * <p>VectorGrid 对 `!response.ok` 的处理是**静默返回空图层** —— 404 / 401 / 500 在页面上
     * 全表现为"没有数据", 不报错也不打日志, 排查时完全没有抓手 (端点没部署? 鉴权没过? 真的没面?)。
     * 这里在本会话第一片上多打一次请求, 把真实状态码摆出来; 只跑一次, 代价可忽略。
     */
    let probed = false;
    const probeOnce = (coords: any) => {
        if (probed) {
            return;
        }
        probed = true;
        const url = (L as any).Util.template(layer._url, { ...coords, ...layer.options });
        void fetch(url, { headers: authHeaders() })
            .then((res) => {
                if (res.ok || res.status === 204) {
                    // eslint-disable-next-line no-console
                    console.info('[aoi-tiles] 瓦片端点连通', { url, status: res.status });
                    return;
                }
                // eslint-disable-next-line no-console
                console.error('[aoi-tiles] 瓦片请求失败', { url, status: res.status });
                const hint = res.status === 404
                    ? '端点不存在 (map-service / api-server 未部署, 或本地未代理 /api)'
                    : res.status === 401 || res.status === 403
                        ? '鉴权未通过 (登录态 / AOI_PLATFORM 资源权限)'
                        : '服务端错误, 详见后端日志';
                message.error(`AOI 轮廓加载失败: HTTP ${res.status} —— ${hint}`, 6);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('[aoi-tiles] 瓦片请求异常', err);
                message.error('AOI 轮廓加载失败: 网络异常, 详见控制台', 6);
            });
    };

    // 包住 VectorGrid 自己的取瓦片实现 (它内部就是 fetch), 做五件事:
    // 1) 修正 z; 2) 并发闸门; 3) 每次请求现读 token 塞进 Authorization (图层长驻, 不能用建层时的快照);
    // 4) 按片计算版本号 (范围失效); 5) 首片探针, 让失败可见。
    // options.v / fetchOptions 是同步读写 (originalGetTile 内同步拼 URL 后才 await fetch), 无竞态。
    const originalGetTile = layer._getVectorTilePromise.bind(layer);
    layer._getVectorTilePromise = (coords: any) => gate(() => {
        layer.options.fetchOptions = { headers: authHeaders() };
        const fixed = tileCoordsWithOffset(coords);
        layer.options.v = versionFor(fixed);
        probeOnce(fixed);
        return originalGetTile(fixed);
    });

    layer.on('click', (e: any) => {
        if (options.isPickingOrDrawing()) {
            return;
        }
        const props = e?.layer?.properties as TileFeatureProps | undefined;
        if (!props) {
            return;
        }
        // 记下级别: 选中样式在 detail 回来前就要按级别上色
        levelByAoiId.set(props.aoiId, props.level);
        L.DomEvent.stopPropagation(e);
        options.onSelect(props.aoiId, props.zipcode ?? null);
    });

    return {
        layer,
        setSelected: (aoiId, levelEnum) => {
            const previous = selectedId;
            selectedId = aoiId;
            if (aoiId != null) {
                // 级别来源优先级: 外部传入 (detail) > 点击时缓存的 feature 属性 > 未知 (深灰兜底)
                const cached = levelByAoiId.get(aoiId);
                selectedLevelResolved = levelEnum
                    ?? (cached === 0 ? 'LEVEL_1' : cached === 1 ? 'LEVEL_2' : null);
            }
            if (previous != null && previous !== aoiId) {
                applyFeatureOverride(previous);
            }
            if (aoiId != null) {
                applyFeatureOverride(aoiId);
            }
        },
        setHidden: (aoiId) => {
            if (hiddenId === aoiId) {
                return;
            }
            const previous = hiddenId;
            hiddenId = aoiId;
            // 解除隐藏的面可能仍处于选中态, applyFeatureOverride 会自动恢复其选中样式
            if (previous != null) {
                applyFeatureOverride(previous);
            }
            if (aoiId != null) {
                applyFeatureOverride(aoiId);
            }
        },
        setConflicts: (aoiIds) => {
            // 差量更新: 页面在保存/绘制/重置等很多路径上都会传空数组"清一次冲突",
            // 集合没变 (最常见是 空→空) 必须直接返回 —— 以前这里 redraw, 每次清空 = 整屏重拉瓦片
            const next = new Set(aoiIds);
            const changed: number[] = [];
            next.forEach((id) => {
                if (!conflictIds.has(id)) {
                    changed.push(id);
                }
            });
            conflictIds.forEach((id) => {
                if (!next.has(id)) {
                    changed.push(id);
                }
            });
            if (changed.length === 0) {
                return;
            }
            conflictIds = next;
            changed.forEach(applyFeatureOverride);
        },
        setSourceVisibility: (next) => {
            visibility = next;
            layer.redraw();
        },
        setIncludeUnverified: (include) => {
            const next = include ? 'true' : 'false';
            if (layer.options.includeUnverified === next) {
                return;
            }
            layer.options.includeUnverified = next;
            // URL 变了才 redraw (整层重拉); 开关是低频显式操作, 可以接受
            layer.redraw();
        },
        setVersion: (next, bbox) => {
            if (next <= lastAppliedVersion) {
                return;
            }
            lastAppliedVersion = next;
            if (bbox) {
                invalidations.push({
                    bbox: [
                        bbox[0] - INVALIDATION_PAD_DEG,
                        bbox[1] - INVALIDATION_PAD_DEG,
                        bbox[2] + INVALIDATION_PAD_DEG,
                        bbox[3] + INVALIDATION_PAD_DEG,
                    ],
                    v: next,
                });
                // 防御: 一次会话疯狂编辑几百次后列表才可能到这个量级, 塌缩成全局失效兜底
                if (invalidations.length > 200) {
                    invalidations.length = 0;
                    baseVersion = next;
                }
            } else {
                // 没有 bbox (调用方拿不到几何) 退回全局失效
                baseVersion = next;
            }
            // redraw 重取可视瓦片: 未被失效的片 URL 不变, 直接命中浏览器缓存 (毫秒级);
            // 只有 bbox 压到的 1~4 片真正走网络
            layer.redraw();
        },
        destroy: () => {
            layer.off('click');
            layer.remove();
        },
    };
}
